import { ArrowRight, Hash, Plug, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { integrationsApi } from '../lib/api.js';
import { cx } from '../lib/cx.js';
import { roleAtLeast, useRole } from '../lib/session.js';
import { NAV_ITEMS } from './nav.js';

interface Command {
  id: string;
  label: string;
  hint?: string;
  icon: typeof Search;
  run: () => void;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Ctrl/Cmd+K palette: jump to a page, open an alarm by id, or open an
 * integration by name. Integrations are fetched lazily the first time the
 * palette opens so the shell stays cheap.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const role = useRole();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [integrations, setIntegrations] = useState<{ id: string; name: string; type: string }[]>(
    [],
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || integrations.length > 0) return;
    let cancelled = false;
    void integrationsApi
      .listAll()
      .then((items) => {
        if (!cancelled) {
          setIntegrations(items.map((i) => ({ id: i.id, name: i.name, type: i.source_type })));
        }
      })
      .catch(() => {
        /* the palette still navigates without integrations */
      });
    return () => {
      cancelled = true;
    };
  }, [open, integrations.length]);

  const commands = useMemo<Command[]>(() => {
    const trimmed = query.trim();
    const needle = trimmed.toLowerCase();
    const out: Command[] = [];

    if (UUID.test(trimmed)) {
      out.push({
        id: `alarm-${trimmed}`,
        label: `Open alarm ${trimmed.slice(0, 8)}…`,
        hint: 'Alarm id',
        icon: Hash,
        run: () => {
          void navigate(`/alarms/${trimmed}`);
        },
      });
    }

    for (const item of NAV_ITEMS) {
      // The same gate the sidebar applies: a page the role cannot open is not offered.
      if (item.minRole && !roleAtLeast(role, item.minRole)) continue;
      if (needle && !item.label.toLowerCase().includes(needle)) continue;
      out.push({
        id: `nav-${item.to}`,
        label: item.label,
        hint: 'Page',
        icon: item.icon,
        run: () => {
          void navigate(item.to);
        },
      });
    }

    for (const integration of integrations) {
      if (needle && !integration.name.toLowerCase().includes(needle)) continue;
      out.push({
        id: `integration-${integration.id}`,
        label: integration.name,
        hint: `Integration · ${integration.type}`,
        icon: Plug,
        run: () => {
          void navigate(`/integrations/${integration.id}`);
        },
      });
    }

    return out.slice(0, 40);
  }, [query, integrations, navigate, role]);

  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(0, commands.length - 1)));
  }, [commands.length]);

  if (!open) return null;

  const runActive = (): void => {
    commands[active]?.run();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center p-4"
      style={{ background: 'var(--scrim)' }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="vam-enter mt-[12vh] w-full max-w-[560px] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-2)]"
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3">
          <Search className="h-4 w-4 shrink-0 text-[var(--text-3)]" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search pages, integrations, or paste an alarm id"
            aria-label="Search"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            aria-activedescendant={commands[active]?.id}
            className="h-11 w-full bg-transparent text-[var(--text-md)] text-[var(--text)] outline-none placeholder:text-[var(--text-3)]"
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((i) => (i + 1) % Math.max(1, commands.length));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((i) => (i - 1 + commands.length) % Math.max(1, commands.length));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                runActive();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
              }
            }}
          />
          <kbd className="shrink-0 rounded-[var(--radius-sm)] border border-[var(--border)] px-1.5 py-0.5 text-[var(--text-xs)] text-[var(--text-3)]">
            Esc
          </kbd>
        </div>
        <ul
          id="command-palette-list"
          role="listbox"
          ref={listRef}
          className="max-h-[50vh] overflow-y-auto py-1"
        >
          {commands.length === 0 && (
            <li className="px-3 py-6 text-center text-[var(--text-base)] text-[var(--text-3)]">
              Nothing matches “{query}”.
            </li>
          )}
          {commands.map((command, index) => {
            const Icon = command.icon;
            return (
              <li key={command.id} id={command.id} role="option" aria-selected={index === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(index)}
                  onClick={() => {
                    command.run();
                    onClose();
                  }}
                  className={cx(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[var(--text-base)]',
                    index === active ? 'bg-[var(--surface-2)]' : 'bg-transparent',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-[var(--text-3)]" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-[var(--text)]">
                    {command.label}
                  </span>
                  {command.hint && (
                    <span className="shrink-0 text-[var(--text-sm)] text-[var(--text-3)]">
                      {command.hint}
                    </span>
                  )}
                  {index === active && (
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-[var(--text-3)]" aria-hidden />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
